import { useState, useEffect } from 'react';
import { createApiClient } from '../lib/api-client';
import { useApiStore } from '../store/api';
import { ActivityFilters } from './activity/ActivityFilters';
import { ActivityTable } from './activity/ActivityTable';
import { Button } from './ui/Button';
import { EmptyState } from './ui/EmptyState';
import { Spinner } from './ui/Spinner';

interface ActivityLog {
  id: string;
  timestamp: string;
  action_type: string;
  method: string;
  path: string;
  auth_type: 'jwt' | 'api_key' | 'sse_token' | 'none';
  user_id: string | null;
  agent_id: string | null;
  api_key_id: string | null;
  status_code: number;
  duration_ms: number;
  ip_address: string | null;
  user_agent: string | null;
  error_type: string | null;
  error_message: string | null;
}

interface ActivityProps {
  onBack: () => void;
}

export default function Activity({ onBack }: ActivityProps) {
  const { apiUrl } = useApiStore();
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [actionType, setActionType] = useState('');
  const [statusCode, setStatusCode] = useState('');
  const [limit, setLimit] = useState(50);

  useEffect(() => {
    loadActivityLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Re-load when filters change
  }, [actionType, statusCode, limit]);

  const loadActivityLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      const client = createApiClient(apiUrl);

      const queryParams: Record<string, string | number> = { limit };
      if (actionType) queryParams.action_type = actionType;
      if (statusCode) queryParams.status_code = Number(statusCode);

      const { data, error } = await client.GET('/v1/activity-logs', {
        params: { query: queryParams },
      });

      if (error || !data) {
        throw new Error('Failed to load activity logs');
      }

      setLogs('logs' in data ? (data.logs as ActivityLog[]) : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setActionType('');
    setStatusCode('');
    setLimit(50);
  };

  return (
    <div className="min-h-screen bg-neutral-0 dark:bg-neutral-900">
      {/* Header */}
      <header className="bg-neutral-50 dark:bg-neutral-800 border-b border-neutral-300 dark:border-neutral-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <Button variant="secondary" onClick={onBack} className="mr-4">
              ← Back
            </Button>
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
              Activity Logs
            </h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <ActivityFilters
          actionType={actionType}
          statusCode={statusCode}
          limit={limit}
          onActionTypeChange={setActionType}
          onStatusCodeChange={setStatusCode}
          onLimitChange={setLimit}
          onClear={handleClearFilters}
          onRefresh={loadActivityLogs}
        />

        {/* Error State */}
        {error && (
          <div className="bg-red-900/20 border border-red-700 rounded-lg p-4 mb-6">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <Spinner size="lg" className="mx-auto" />
            <p className="mt-4 text-neutral-600 dark:text-neutral-400">Loading activity logs...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && logs.length === 0 && (
          <EmptyState
            type="no-data"
            title="No activity logs found"
            description="Try adjusting your filters or refresh to see new logs."
          />
        )}

        {/* Activity Logs Table */}
        {!loading && !error && logs.length > 0 && (
          <>
            <ActivityTable logs={logs} />

            {/* Log Count Footer */}
            <div className="mt-4 text-sm text-neutral-600 dark:text-neutral-400 text-center">
              Showing {logs.length} log{logs.length !== 1 ? 's' : ''}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
